
import piexif from 'piexifjs';

// Max file size to attempt patching in memory (30MB).
// Larger files will risk OOM on mobile devices during binary string conversion.
const MAX_PATCH_SIZE_BYTES = 30 * 1024 * 1024;

export interface PatchMetadata {
    lat?: number;
    lng?: number;
    date?: string; // ISO string or similar
    originalDate?: string;
}

/**
 * Helper to convert Degrees Decimal to DMS (Degrees, Minutes, Seconds) format required by EXIF.
 */
function toDMS(deg: number): [[number, number], [number, number], [number, number]] {
    const d = Math.abs(deg);
    const degrees = Math.floor(d);
    const minutes = Math.floor((d - degrees) * 60);
    // Seconds with high precision
    const seconds = Math.round((d - degrees - minutes / 60) * 3600 * 10000);

    return [
        [degrees, 1],
        [minutes, 1],
        [seconds, 10000]
    ];
}

/**
 * Reader to convert Blob to BinaryString (required by piexifjs).
 * This is the most memory-intensive part.
 */
function blobToBinaryString(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            resolve(e.target?.result as string);
        };
        reader.onerror = reject;
        reader.readAsBinaryString(blob);
    });
}

/**
 * Converts BinaryString back to Uint8Array/Blob.
 */
function binaryStringToUint8Array(binStr: string): Uint8Array {
    const len = binStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binStr.charCodeAt(i);
    }
    return bytes;
}

/**
 * Main function to patch EXIF data into an image Blob.
 * Returns a new Blob with updated metadata.
 * Throws error if file is too large or format is unsupported.
 */
export async function patchExif(file: File | Blob, meta: PatchMetadata): Promise<Blob> {
    // 1. Safety Check: OOM Prevention
    if (file.size > MAX_PATCH_SIZE_BYTES) {
        throw new Error(`File too large for client-side patching (${(file.size / 1024 / 1024).toFixed(1)}MB). Limit is ${MAX_PATCH_SIZE_BYTES / 1024 / 1024}MB.`);
    }

    // 2. Read File
    const originalBinary = await blobToBinaryString(file);

    // 3. Load existing EXIF or create empty
    let exifObj;
    try {
        exifObj = piexif.load(originalBinary);
    } catch (e) {
        // If load fails (e.g. no exif), create strict structure
        exifObj = { "0th": {}, "Exif": {}, "GPS": {}, "1st": {}, "thumbnail": null };
    }

    // 4. Update GPS
    if (meta.lat !== undefined && meta.lng !== undefined) {
        exifObj["GPS"][piexif.GPSIFD.GPSLatitudeRef] = meta.lat < 0 ? 'S' : 'N';
        exifObj["GPS"][piexif.GPSIFD.GPSLatitude] = toDMS(meta.lat);
        exifObj["GPS"][piexif.GPSIFD.GPSLongitudeRef] = meta.lng < 0 ? 'W' : 'E';
        exifObj["GPS"][piexif.GPSIFD.GPSLongitude] = toDMS(meta.lng);
    }

    // 5. Update Date (DateTimeOriginal)
    // Format: "YYYY:MM:DD HH:MM:SS"
    if (meta.date) {
        try {
            const d = new Date(meta.date);
            // Ensure valid date
            if (!isNaN(d.getTime())) {
                const pad = (n: number) => n.toString().padStart(2, '0');
                const exifDate = `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                exifObj["Exif"][piexif.ExifIFD.DateTimeOriginal] = exifDate;
            }
        } catch (e) {
            console.warn("Invalid date for EXIF patch", meta.date);
        }
    }

    // 6. Dump to bytes
    const exifBytes = piexif.dump(exifObj);

    // 7. Insert into binary
    // piexif.insert returns the new binary string (JPEG)
    const newBinary = piexif.insert(exifBytes, originalBinary);

    // 8. Convert back to Blob
    const newBytes = binaryStringToUint8Array(newBinary);
    return new Blob([newBytes as any], { type: file.type || 'image/jpeg' });
}
